import { DeleteOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { Badge, Heading } from '@orbita-ui/core';
import { Button, Form, Input, Modal, Popconfirm, Select, Space, Table } from 'antd';
import { format } from 'date-fns';
import useCreateUser from 'hooks/data/useCreateUser';
import useGetAllGroups from 'hooks/data/useGetAllGroups';
import usetGetAllUsers from 'hooks/data/useGetAllUsers';
import useDeleteUser from 'hooks/delete/useDeleteUser';
import useAuthService from 'hooks/useAuthService';
import useToastContext from 'hooks/useToastContext';
import { User } from 'interfaces/users';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { camelCaseObjectToSnakeCase } from 'utils/camelCaseToSnakeCase';
import { HttpStatusCode, RealmRoles } from '../../../constants';
import * as S from './users.styles';

type NewUserFormInputs = {
  userName: string;
  email: string;
  firstName: string;
  lastName: string;
  groups: string[];
};

interface UserCreateFormProps {
  open: boolean;
  onCancel: () => void;
  existingUsers: unknown;
}

const UserCreateForm: React.FC<UserCreateFormProps> = ({ open, onCancel, existingUsers }) => {
  const { groups } = useGetAllGroups(0, 100);
  const [form] = Form.useForm();
  const toast = useToastContext();
  const { isLoading: createUserLoading, createUser } = useCreateUser();

  const onSubmit = async (data) => {
    try {
      const res = await createUser(camelCaseObjectToSnakeCase({ ...data, groups: data.groups }));
      if (res.status === HttpStatusCode.Created) {
        toast.success('Exito', 'Se ha creado el usuario con exito!');
        form.resetFields();
        onCancel();
      }
    } catch (e) {
      console.error(e);
      toast.error('Error', 'No se ha podido crear el usuario, intentar nuevamente.');
    }
  };

  return (
    <Modal
      open={open}
      title="Crear nuevo usuario"
      okText="Crear"
      okButtonProps={{ loading: createUserLoading }}
      cancelText="Cancelar"
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            onSubmit(values);
          })
          .catch((info) => {
            console.error('Validate Failed:', info);
          });
      }}
    >
      <Form form={form} layout="vertical" name="form_in_modal">
        <Form.Item<NewUserFormInputs>
          name="userName"
          label="Nombre de usuario"
          rules={[{ required: true, message: 'Por favor ingresar un nombre de usuario.' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item<NewUserFormInputs>
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Por favor ingresar un email.' },
            { type: 'email', message: 'Por favor ingresar un email valido.' },
            () => ({
              validator(_, value) {
                if (!existingUsers?.find((user) => user?.email === value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Usuario ya existe, ingrese otro email.'));
              },
            }),
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item<NewUserFormInputs>
          name="firstName"
          label="Nombre"
          rules={[{ required: true, message: 'Por favor ingresar un nombre.' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item<NewUserFormInputs>
          name="lastName"
          label="Apellido"
          rules={[{ required: true, message: 'Por favor ingresar un apellido.' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item<NewUserFormInputs>
          name="groups"
          label="Grupo"
          rules={[{ required: true, message: 'Por favor seleccionar uno.' }]}
        >
          <Select mode="multiple" options={groups?.map((group) => ({ label: group?.name, value: group?.path }))} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const SettingsUsers = () => {
  const navigate = useNavigate();
  const toast = useToastContext();
  const { pathname } = useLocation();
  const { deleteUser, isLoading: deleteUserLoading } = useDeleteUser();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const { users, pageInfo, isLoading, execute: retryGetUser} = usetGetAllUsers(paginationModel.page, paginationModel.pageSize);
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const { userHasRole } = useAuthService();
  const handleCreateUserClick = () => {
    setShowNewUserModal(true);
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPaginationModel({ page: page - 1, pageSize });
  };

  const confirm = async (user: User) => {
    try {
      const res = await deleteUser(user.id);
      if (res.status === HttpStatusCode.NoContent) {
        toast.success('Usuario eliminado!', `El usuario ${user.email} se elimino exitosamente.`);
        retryGetUser();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (e) {
      console.error(e);
      toast.error('Error!', 'El usuario no se ha eliminado. Intentar nuevamente.');
    }
  };

  return (
    <>
      <Heading bold size="xl">
        Mis Usuarios
      </Heading>
      <S.UsersActionsContainer>
        {userHasRole([RealmRoles.UsersAllWrite]) && (
          <Button icon={<PlusOutlined />} onClick={handleCreateUserClick} type="primary">
            Crear usuario
          </Button>
        )}
      </S.UsersActionsContainer>
      <Table
        scroll={{ x: true }}
        loading={isLoading}
        columns={[
          {
            dataIndex: 'id',
            title: 'ID',
            key: 'id',
          },
          {
            dataIndex: 'email',
            title: 'Email',
            key: 'email',
          },
          {
            dataIndex: 'user_name',
            title: 'Nombre de Usuario',
            key: 'user_name',
          },
          {
            dataIndex: 'created_timestamp',
            title: 'Fecha de Creacion',
            key: 'created_timestamp',
            render: (_, record) => {
              const createdTimestamp = record?.created_timestamp;
              return format(new Date(createdTimestamp), 'dd-MM-yyyy');
            },
          },
          {
            dataIndex: 'enabled',
            title: 'Estado',
            key: 'enabled',
            render: (_, record) => {
              const enabled = record?.enabled;
              return (
                <Badge
                  label={enabled ? 'Activado' : 'Desactivado'}
                  emphasis="medium"
                  color={enabled ? 'green' : 'red'}
                />
              );
            },
          },
          {
            dataIndex: 'actions',
            title: 'Acciones',
            key: 'actions',
            render: (_, record) => {
              return (
                <Space>
                  <Button
                    size="small"
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`${pathname}/users/${record?.id}`)}
                  >
                    Ver
                  </Button>
                  <Popconfirm
                    title="Eliminar usuario"
                    description={`Estas seguro que queres eliminar el usuario ${record?.email}?`}
                    onConfirm={() => {
                      confirm(record);
                    }}
                    okText="Si"
                    cancelText="No"
                    okButtonProps={{ loading: deleteUserLoading }}
                  >
                    <Button size="small" type="link" icon={<DeleteOutlined />} danger>
                      Eliminar
                    </Button>
                  </Popconfirm>
                </Space>
              );
            },
          },
        ]}
        dataSource={users ?? []}
        pagination={{
          total: pageInfo?.total,
          showTotal: (total) => pageInfo?.total !== undefined ? `Total: ${total}` : ``,
          defaultPageSize: 10,
          defaultCurrent: 1,
          onChange: handlePaginationChange,
          pageSizeOptions: ['5', '10', '25', '50', '100'],
          showSizeChanger: true,
        }}
      />
      <UserCreateForm open={showNewUserModal} onCancel={() => setShowNewUserModal(false)} existingUsers={users} />
    </>
  );
};

export default SettingsUsers;
